
      import { ShiftingOriginGraph } from '@folkjs/labs/ShiftingOriginGraph';
      import { isScreenCoveredByRectangle } from '@folkjs/geometry/Rect2D';

      // Define constants for node dimensions
      const NODE_WIDTH = 500 * 2;
      const NODE_HEIGHT = 300 * 2;
      // Define zoom limits for when the reference node doesn't change
      const MAX_ZOOM_LEVEL = 100.0; // Maximum zoom level (zoomed in)

      let currentZoomLevel = 1.0; // Track the current zoom level
      let lastOriginNodeId = null; // Track the last reference node

      // Caches for performance optimization
      const nodeContentCache = {}; // Cache for node content templates
      const linkPositionsCache = {}; // Cache for link positions within nodes

      // Node element pool for performance optimization
      class NodePool {
        constructor() {
          this.fullPools = new Map(); // Map of nodeId -> array of available full elements
          this.simplePools = new Map(); // Map of nodeId -> array of available simple elements
          this.activeElements = new Map(); // Map of element -> {nodeId, isSimple}
        }

        // Get an element for a specific node type (create if needed)
        get(nodeId, isSimple) {
          // Get the appropriate pool
          const poolMap = isSimple ? this.simplePools : this.fullPools;

          // Initialize pool for this node type if it doesn't exist
          if (!poolMap.has(nodeId)) {
            poolMap.set(nodeId, []);
          }

          const pool = poolMap.get(nodeId);
          let element = null;

          // Get an element from the pool if available
          if (pool.length > 0) {
            element = pool.pop();
          } else {
            // Create a new element if none available
            element = createNodeElement(nodeId, 0, isSimple);
          }

          // Mark as active
          this.activeElements.set(element, { nodeId, isSimple });
          return element;
        }

        // Release an element back to the pool
        release(element) {
          if (this.activeElements.has(element)) {
            const { nodeId, isSimple } = this.activeElements.get(element);
            this.activeElements.delete(element);

            // Return to the appropriate pool
            const poolMap = isSimple ? this.simplePools : this.fullPools;
            poolMap.get(nodeId).push(element);

            return true;
          }
          return false;
        }

        // Release all elements not in the active set
        releaseUnused(activeSet) {
          const elementsToRelease = [];

          // Find elements to release
          for (const [element, _] of this.activeElements) {
            if (!activeSet.has(element)) {
              elementsToRelease.push(element);
            }
          }

          // Release them
          for (const element of elementsToRelease) {
            this.release(element);
            if (element.parentNode) {
              element.parentNode.removeChild(element);
            }
          }
        }
      }

      // Create the node pool
      const nodePool = new NodePool();

      // Define nodes as an array for our simple 3-node cycle
      const nodesArray = [
        {
          id: 'nodeA',
          data: {
            title: 'Aetheria',
            color: 'rgb(70, 50, 100)',
            content: `
              <div class="wiki-content">
                <h3>Aetheria</h3>
                <div class="summary">A floating city-state in the upper atmosphere of Jovian-9, renowned for its advanced teleportation technology and crystalline architecture.</div>

                <p>Established in 2387 as a scientific research outpost, Aetheria evolved into a sovereign technological haven within decades. The city hovers within the upper cloud layers of <a href="#" class="zui-link-inline" data-target="nodeD">Jovian-9</a>, protected by a powerful energy field that maintains Earth-like conditions within its boundaries.</p>

                <h4>Notable Locations</h4>
                <p>The <a href="#" class="zui-link-inline" data-target="nodeE">Central Nexus</a> serves as both governmental center and research facility. The sprawling <strong>Cloudwalker District</strong> houses most of the 50,000 permanent residents, while the <strong>Prism Gardens</strong> feature exotic flora that thrive in the unusual atmospheric conditions.</p>

                <h4>Technology</h4>
                <p>Aetheria's prosperity is largely built upon its revolutionary <a href="#" class="zui-link-inline" data-target="nodeB">Quantum Foldspace</a> technologies, which have made it the hub of interplanetary commerce in the outer solar system. The city's <a href="#" class="zui-link-inline" data-target="nodeF">Crystalline Architecture</a> serves both aesthetic and functional purposes.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeB',
          data: {
            title: 'Quantum Foldspace',
            color: 'rgb(40, 80, 120)',
            content: `
              <div class="wiki-content">
                <h3>Quantum Foldspace</h3>
                <div class="summary">A theoretical dimension that enables near-instantaneous travel between distant points in conventional space-time through dimensional compression.</div>

                <p>First theorized by <a href="#" class="zui-link-inline" data-target="nodeH">Dr. Elara Venn</a> in 2412, Quantum Foldspace exists as a parallel dimension where conventional space-time is compressed to an extraordinary degree. <a href="#" class="zui-link-inline" data-target="nodeA">Aetherian</a> scientists developed the revolutionary Venn-Katori apparatus that creates temporary "folds" in reality, allowing matter to traverse vast distances through this dimensional shortcut.</p>

                <h4>Observable Properties</h4>
                <p>To human observers, Foldspace appears as a luminescent azure void with visible ripples of energy that correspond to gravitational waves in normal space. Physical laws differ significantly from our universe, with time dilation effects making a typical journey feel subjectively instantaneous regardless of the objective distance covered.</p>

                <h4>Applications</h4>
                <p>Beyond transportation, Foldspace technology has enabled breakthroughs in <a href="#" class="zui-link-inline" data-target="nodeI">Temporal Communications</a> and energy generation. The <a href="#" class="zui-link-inline" data-target="nodeC">Mist Sovereigns</a> have developed unique applications of the technology for exploring distant gas giants.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeC',
          data: {
            title: 'The Mist Sovereigns',
            color: 'rgb(60, 100, 70)',
            content: `
              <div class="wiki-content">
                <h3>The Mist Sovereigns</h3>
                <div class="summary">A collective intelligence of sentient gaseous entities native to the lower atmosphere of Jovian-9, capable of manipulating atmospheric conditions with remarkable precision.</div>

                <p>First contacted in 2430, the Mist Sovereigns are a distributed consciousness composed of billions of specialized ionized gas particles that form a collective mind. They communicate through complex patterns of modulated electrical discharges that <a href="#" class="zui-link-inline" data-target="nodeA">Aetherian</a> linguists have only partially decoded. Individual Sovereigns can extend across hundreds of kilometers but typically condense to more localized forms when interacting with humans.</p>

                <h4>Culture and Society</h4>
                <p>The Sovereigns maintain an ancient tradition of <a href="#" class="zui-link-inline" data-target="nodeK">Storm Poetry</a>, a complex art form that combines atmospheric manipulation with electrical discharges to create immersive experiences. They revere the planet's <a href="#" class="zui-link-inline" data-target="nodeD">Great Red Vortex</a> as a sacred site.</p>

                <h4>Relations with Aetheria</h4>
                <p>Initial encounters were marked by hostility as the Sovereigns perceived Aetherian energy fields as an intrusion into their domain. After the <a href="#" class="zui-link-inline" data-target="nodeG">Diplomatic Crisis of 2433</a>, a cautious alliance was established. The Sovereigns now maintain a complex symbiotic relationship with Aetheria, exchanging their unparalleled knowledge of atmospheric science for limited access to <a href="#" class="zui-link-inline" data-target="nodeB">foldspace technology</a>, which they use to explore distant gas giants.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeD',
          data: {
            title: 'Jovian-9',
            color: 'rgb(120, 60, 40)',
            content: `
              <div class="wiki-content">
                <h3>Jovian-9</h3>
                <div class="summary">The ninth planet in the Jovian system, a massive gas giant characterized by its stunning banded atmosphere and the famous Great Red Vortex.</div>

                <p>Discovered in 2189 by the Galileo-Cassini deep space telescope, Jovian-9 is approximately 2.3 times the size of Jupiter with 5.7 times its mass. Its rapid rotation (a day lasts just 7.8 Earth hours) creates pronounced atmospheric bands of swirling gases in vibrant hues of amber, turquoise, and violet.</p>

                <h4>The Great Red Vortex</h4>
                <p>The planet's most distinctive feature is the Great Red Vortex, an anticyclonic storm system that has persisted for at least 400 years. Spanning roughly 30,000 kilometers in diameter, the vortex serves as a spiritual center for the native <a href="#" class="zui-link-inline" data-target="nodeC">Mist Sovereigns</a> and generates powerful electromagnetic pulses that have complicated human exploration.</p>

                <h4>Human Settlements</h4>
                <p>The floating city-state of <a href="#" class="zui-link-inline" data-target="nodeA">Aetheria</a> remains the only permanent human settlement, hovering in the upper atmosphere. Several research outposts orbit the planet, including the <a href="#" class="zui-link-inline" data-target="nodeL">Galileo Research Station</a>, which conducts ongoing atmospheric studies.</p>

                <h4>Resources</h4>
                <p>Jovian-9's atmosphere is rich in valuable isotopes used in <a href="#" class="zui-link-inline" data-target="nodeB">quantum foldspace</a> technology, which has driven much of the economic interest in the planet despite the challenging conditions.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeE',
          data: {
            title: 'Central Nexus',
            color: 'rgb(100, 30, 100)',
            content: `
              <div class="wiki-content">
                <h3>Central Nexus</h3>
                <div class="summary">The geometric and governmental center of Aetheria, housing both its administrative functions and primary research facilities.</div>

                <p>The Central Nexus rises from the center of <a href="#" class="zui-link-inline" data-target="nodeA">Aetheria</a> as a towering spire of prismatic crystal, standing over 500 meters tall. Its distinctive helical structure is composed of self-replicating <a href="#" class="zui-link-inline" data-target="nodeF">crystalline matrices</a> that gradually repair and extend themselves over time, making the building a living monument to Aetherian engineering.</p>

                <h4>Governance</h4>
                <p>The upper levels house the chambers of the <a href="#" class="zui-link-inline" data-target="nodeG">Atmospheric Council</a>, where the seven elected representatives manage Aetheria's civic affairs and diplomatic relations with the <a href="#" class="zui-link-inline" data-target="nodeC">Mist Sovereigns</a>. Public observation decks allow citizens to witness council proceedings through transparent crystal walls.</p>

                <h4>Research Divisions</h4>
                <p>The middle and lower sections contain the primary scientific facilities, including the Department of <a href="#" class="zui-link-inline" data-target="nodeB">Quantum Studies</a> where Dr. <a href="#" class="zui-link-inline" data-target="nodeH">Elara Venn</a> conducted her groundbreaking experiments. The controversial <a href="#" class="zui-link-inline" data-target="nodeI">Temporal Communications</a> lab occupies a shielded sublevel.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeF',
          data: {
            title: 'Crystalline Architecture',
            color: 'rgb(140, 140, 180)',
            content: `
              <div class="wiki-content">
                <h3>Crystalline Architecture</h3>
                <div class="summary">A revolutionary construction methodology using programmable, self-organizing crystal structures that respond to both environmental conditions and deliberate control inputs.</div>

                <p>Developed in 2402 by architect <a href="#" class="zui-link-inline" data-target="nodeM">Sarai Nimura</a>, Crystalline Architecture forms the distinctive aesthetic of <a href="#" class="zui-link-inline" data-target="nodeA">Aetheria</a>. The buildings utilize specially engineered crystal matrices that grow according to programmed patterns, allowing structures to be "cultivated" rather than constructed in the traditional sense.</p>

                <h4>Technical Properties</h4>
                <p>The crystals are composed primarily of silicon-carbon compounds with trace elements that enable semi-organic growth patterns. Each structure contains a network of nanoscale control nodes that can trigger limited reconfiguration, allowing buildings to adapt to changing weather conditions on <a href="#" class="zui-link-inline" data-target="nodeD">Jovian-9</a> or expand to accommodate population growth.</p>

                <h4>Notable Examples</h4>
                <p>The <a href="#" class="zui-link-inline" data-target="nodeE">Central Nexus</a> represents the pinnacle of this architectural style, but other striking examples include the Resonance Amphitheater and the intricate latticework of the <a href="#" class="zui-link-inline" data-target="nodeN">Suspended Gardens</a>, where crystal structures have been programmed to provide optimal growing conditions for terrestrial plants.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeG',
          data: {
            title: 'The Atmospheric Council',
            color: 'rgb(90, 110, 130)',
            content: `
              <div class="wiki-content">
                <h3>The Atmospheric Council</h3>
                <div class="summary">The governing body of Aetheria, composed of seven elected representatives who manage both internal affairs and diplomatic relations with the Mist Sovereigns.</div>

                <p>Established in 2392 after <a href="#" class="zui-link-inline" data-target="nodeA">Aetheria</a> declared independence from Earth's Colonial Administration, the Atmospheric Council operates on a consensus model rather than majority rule. Council members serve five-year terms with a two-term limit, ensuring regular rotation of leadership.</p>

                <h4>The Diplomatic Crisis of 2433</h4>
                <p>The Council faced its greatest challenge during the <a href="#" class="zui-link-inline" data-target="nodeC">Mist Sovereign</a> conflict of 2433, when energy field expansions into the lower atmosphere were interpreted as territorial aggression by the native entities. Council Chair <a href="#" class="zui-link-inline" data-target="nodeO">Takashi Moreau</a> personally descended into the storm layers of <a href="#" class="zui-link-inline" data-target="nodeD">Jovian-9</a> to negotiate, establishing the historic Atmospheric Accord that has maintained peace ever since.</p>

                <h4>Current Policies</h4>
                <p>Today's Council focuses on sustainable growth, maintaining equilibrium with the planet's atmospheric systems, and expanding <a href="#" class="zui-link-inline" data-target="nodeB">foldspace</a> transportation networks. Recent debates have centered on whether to permit <a href="#" class="zui-link-inline" data-target="nodeI">temporal communications</a> research despite concerns from some <a href="#" class="zui-link-inline" data-target="nodeP">Earth factions</a>.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeH',
          data: {
            title: 'Dr. Elara Venn',
            color: 'rgb(160, 120, 40)',
            content: `
              <div class="wiki-content">
                <h3>Dr. Elara Venn</h3>
                <div class="summary">Pioneering physicist and inventor of foldspace theory, whose work revolutionized interplanetary travel and communications.</div>

                <p>Born in the Mars colonies in 2379, Elara Venn demonstrated extraordinary mathematical ability from childhood. After completing dual doctorates in quantum physics and dimensional engineering at Oxford-Luna at just 22, she accepted a research position in <a href="#" class="zui-link-inline" data-target="nodeA">Aetheria</a> to study the unusual electromagnetic properties of <a href="#" class="zui-link-inline" data-target="nodeD">Jovian-9's</a> atmosphere.</p>

                <h4>The Foldspace Breakthrough</h4>
                <p>In 2412, Venn published her revolutionary paper "On the Compression of Spatial Dimensions in Quantum Null Fields," providing the theoretical framework for what would become <a href="#" class="zui-link-inline" data-target="nodeB">Quantum Foldspace</a> technology. Working with engineer Hiroshi Katori, she developed the first functioning Venn-Katori device the following year, demonstrating instantaneous matter transfer over a distance of 10 kilometers.</p>

                <h4>Later Work</h4>
                <p>Following her initial breakthrough, Venn shifted her focus to applications in <a href="#" class="zui-link-inline" data-target="nodeI">temporal physics</a>, exploring the time-dilation effects observed within foldspace. Her controversial collaboration with the <a href="#" class="zui-link-inline" data-target="nodeC">Mist Sovereigns</a> yielded insights into non-human perspectives on time and space that remain influential in theoretical physics. Her current project involves studying potential connections between foldspace and the <a href="#" class="zui-link-inline" data-target="nodeJ">quantum echo</a> phenomenon.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeI',
          data: {
            title: 'Temporal Communications',
            color: 'rgb(60, 150, 160)',
            content: `
              <div class="wiki-content">
                <h3>Temporal Communications</h3>
                <div class="summary">An experimental technology that exploits time dilation effects within foldspace to enable near-instantaneous communication across vast distances.</div>

                <p>While conventional transmissions are limited by light-speed, temporal communications utilize the unique properties of <a href="#" class="zui-link-inline" data-target="nodeB">foldspace</a> to effectively "shortcut" these limitations. The technology emerged from <a href="#" class="zui-link-inline" data-target="nodeH">Dr. Elara Venn's</a> later research, when she discovered that information could be encoded in quantum-entangled particles and transmitted through carefully calibrated foldspace apertures with minimal temporal distortion.</p>

                <h4>Current Implementation</h4>
                <p>The primary temporal communications array is housed in a shielded chamber beneath the <a href="#" class="zui-link-inline" data-target="nodeE">Central Nexus</a> of <a href="#" class="zui-link-inline" data-target="nodeA">Aetheria</a>. While still experimental, the system has successfully maintained real-time connections with Earth outposts over 700 million kilometers away, revolutionizing interplanetary coordination and commerce.</p>

                <h4>Controversy</h4>
                <p>The technology remains controversial among some theoretical physicists who warn of potential causality violations if the system were to experience critical failures. The <a href="#" class="zui-link-inline" data-target="nodeP">Earth Temporal Regulations Commission</a> has repeatedly attempted to restrict research in this field, creating ongoing tension with the <a href="#" class="zui-link-inline" data-target="nodeG">Atmospheric Council</a>, which views the technology as essential to Aetheria's independence and prosperity.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeJ',
          data: {
            title: 'Quantum Echolocation',
            color: 'rgb(90, 50, 140)',
            content: `
              <div class="wiki-content">
                <h3>Quantum Echolocation</h3>
                <div class="summary">A navigation technology that uses quantum resonance patterns to map foldspace pathways and detect objects across dimensional boundaries.</div>

                <p>Developed as a safety system for early <a href="#" class="zui-link-inline" data-target="nodeB">foldspace</a> travel experiments, Quantum Echolocation works by emitting precisely calibrated quantum particles that maintain entanglement with their source. As these particles encounter matter or energy fields, they generate distinctive resonance patterns that can be measured and interpreted to create multidimensional maps.</p>

                <h4>Applications</h4>
                <p>Beyond its primary use in foldspace navigation, the technology has proven valuable for detecting anomalies in normal space-time, particularly in regions with unusual gravitational properties. The <a href="#" class="zui-link-inline" data-target="nodeC">Mist Sovereigns</a> have demonstrated a natural ability that closely resembles this technology, suggesting possible biological implementations.</p>

                <h4>Recent Developments</h4>
                <p>Recent refinements by <a href="#" class="zui-link-inline" data-target="nodeH">Dr. Venn's</a> team at the <a href="#" class="zui-link-inline" data-target="nodeE">Central Nexus</a> have enabled the detection of what appear to be faint "echoes" of past and future states, leading to speculation about potential applications in <a href="#" class="zui-link-inline" data-target="nodeI">temporal observation</a>. These findings remain highly classified by order of the <a href="#" class="zui-link-inline" data-target="nodeG">Atmospheric Council</a>.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeK',
          data: {
            title: 'Storm Poetry',
            color: 'rgb(130, 90, 50)',
            content: `
              <div class="wiki-content">
                <h3>Storm Poetry</h3>
                <div class="summary">An ancient art form practiced by the Mist Sovereigns involving the manipulation of atmospheric conditions to create complex sensory experiences combining visual, auditory, and electrical elements.</div>

                <p>Storm Poetry represents the primary artistic expression of the <a href="#" class="zui-link-inline" data-target="nodeC">Mist Sovereigns</a>, with traditions dating back thousands of years according to their oral histories. Unlike human poetry, Storm Poetry exists as a four-dimensional experience, combining precisely controlled atmospheric disturbances with intricate patterns of electrical discharge that stimulate both emotional and intellectual responses.</p>

                <h4>Human Experience</h4>
                <p>To human observers, Storm Poetry appears as a synchronized dance of colored lightning, swirling cloud formations, and harmonic thunder that creates surprisingly melodic sound patterns. Special observation chambers in <a href="#" class="zui-link-inline" data-target="nodeA">Aetheria's</a> lower levels allow visitors to safely experience these performances, though protective shielding necessarily diminishes some of the electrical components.</p>

                <h4>Cultural Exchange</h4>
                <p>Since the <a href="#" class="zui-link-inline" data-target="nodeG">Diplomatic Crisis of 2433</a>, Storm Poetry performances have become an important component of cultural exchange between humans and Sovereigns. Several human poets and musicians have collaborated with Sovereign artists to create hybrid works that attempt to bridge the perceptual gap between species, with the most successful being composer <a href="#" class="zui-link-inline" data-target="nodeQ">Aria Xiang's</a> "Tempest Symphony."</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeL',
          data: {
            title: 'Galileo Research Station',
            color: 'rgb(70, 100, 120)',
            content: `
              <div class="wiki-content">
                <h3>Galileo Research Station</h3>
                <div class="summary">An orbital scientific facility dedicated to the study of Jovian-9's atmospheric and planetary systems, and home to the most comprehensive database of Jovian meteorological patterns.</div>

                <p>Established in 2395 as a joint venture between the <a href="#" class="zui-link-inline" data-target="nodeP">Earth Science Consortium</a> and <a href="#" class="zui-link-inline" data-target="nodeA">Aetherian</a> researchers, the Galileo Research Station maintains a stable orbit 12,000 kilometers above Jovian-9's upper atmosphere. The toroidal structure houses laboratories, living quarters for 200 scientists and support staff, and powerful sensor arrays that continuously monitor the planet below.</p>

                <h4>Research Focus</h4>
                <p>The station's primary mission involves long-term study of <a href="#" class="zui-link-inline" data-target="nodeD">Jovian-9's</a> unique atmospheric systems, particularly the interactions between the <a href="#" class="zui-link-inline" data-target="nodeC">Mist Sovereigns</a> and the planet's weather patterns. Its massive data archives contain continuous observations dating back nearly 40 years, providing invaluable insights into Jovian climatology.</p>

                <h4>Recent Discoveries</h4>
                <p>In 2439, researchers at Galileo made the surprising discovery that the Great Red Vortex exhibits quantum resonance patterns similar to those used in <a href="#" class="zui-link-inline" data-target="nodeJ">Quantum Echolocation</a>, suggesting it may serve as a natural foldspace aperture under specific conditions. This finding has attracted considerable interest from <a href="#" class="zui-link-inline" data-target="nodeH">Dr. Venn</a> and her team.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeM',
          data: {
            title: 'Sarai Nimura',
            color: 'rgb(150, 80, 100)',
            content: `
              <div class="wiki-content">
                <h3>Sarai Nimura</h3>
                <div class="summary">Revolutionary architect and developer of Crystalline Architecture, whose designs transformed Aetheria from a utilitarian outpost into a floating marvel of living crystal.</div>

                <p>Born in Neo-Tokyo in 2368, Sarai Nimura studied traditional architecture before specializing in biomimetic design at the Institute for Advanced Materials. She arrived in <a href="#" class="zui-link-inline" data-target="nodeA">Aetheria</a> in 2398 during early construction phases, when the settlement still consisted primarily of prefabricated habitation modules.</p>

                <h4>Architectural Revolution</h4>
                <p>Nimura's breakthrough came after studying the silicon-based microorganisms that naturally occur in <a href="#" class="zui-link-inline" data-target="nodeD">Jovian-9's</a> upper atmosphere. By adapting their crystalline growth patterns and combining them with programmable nanomaterials, she developed the first self-organizing <a href="#" class="zui-link-inline" data-target="nodeF">crystal structures</a> that could be "grown" rather than built, dramatically reducing construction resources needed in Aetheria's challenging environment.</p>

                <h4>Legacy</h4>
                <p>The <a href="#" class="zui-link-inline" data-target="nodeE">Central Nexus</a> stands as Nimura's masterpiece, but her influence extends throughout Aetheria in the harmonious integration of function and form. Her current work focuses on developing new crystal variations that can interface directly with the atmospheric manipulation technologies used for communication with the <a href="#" class="zui-link-inline" data-target="nodeC">Mist Sovereigns</a>, potentially enabling architectural features that respond directly to their <a href="#" class="zui-link-inline" data-target="nodeK">Storm Poetry</a>.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeN',
          data: {
            title: 'Suspended Gardens',
            color: 'rgb(60, 130, 70)',
            content: `
              <div class="wiki-content">
                <h3>Suspended Gardens</h3>
                <div class="summary">A botanical wonder suspended beneath Aetheria's main platform, featuring Earth plants growing in crystalline structures attuned to their specific needs.</div>

                <p>Created in 2415 as both a psychological comfort for <a href="#" class="zui-link-inline" data-target="nodeA">Aetherian</a> residents and a practical source of fresh produce, the Suspended Gardens hang beneath the city's main platform, taking advantage of the more humid atmospheric conditions at lower altitudes. Architect <a href="#" class="zui-link-inline" data-target="nodeM">Sarai Nimura</a> designed the complex network of <a href="#" class="zui-link-inline" data-target="nodeF">crystalline</a> growing chambers that automatically adjust light, temperature, and moisture levels to the needs of different plant species.</p>

                <h4>Ecological Systems</h4>
                <p>The Gardens function as a self-sustaining ecosystem, with each chamber carefully balanced to support specific plant communities. Pollination is managed by specially engineered microdrones that mimic the behavior of Earth insects. Water is harvested directly from <a href="#" class="zui-link-inline" data-target="nodeD">Jovian-9's</a> atmosphere through condensation systems that can extract up to 10,000 liters daily during optimal conditions.</p>

                <h4>Cultural Importance</h4>
                <p>Beyond their practical value, the Gardens serve as an important cultural space for Aetherians, many of whom have never visited Earth. Regular gatherings and ceremonies take place among the green spaces, and certain sections have been dedicated to growing plants that hold religious or cultural significance to different Earth traditions. The Gardens also host an annual festival during which the <a href="#" class="zui-link-inline" data-target="nodeC">Mist Sovereigns</a> create gentle <a href="#" class="zui-link-inline" data-target="nodeK">Storm Poetry</a> performances specially designed to stimulate plant growth.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeO',
          data: {
            title: 'Takashi Moreau',
            color: 'rgb(110, 70, 90)',
            content: `
              <div class="wiki-content">
                <h3>Takashi Moreau</h3>
                <div class="summary">Legendary diplomat and former Chair of the Atmospheric Council who negotiated the historic peace accord with the Mist Sovereigns.</div>

                <p>Born to French-Japanese parents aboard a colony ship bound for Mars in 2389, Takashi Moreau demonstrated exceptional linguistic abilities and political acumen from an early age. After completing diplomatic training at the United Earth Academy, he emigrated to <a href="#" class="zui-link-inline" data-target="nodeA">Aetheria</a> in 2418 and quickly rose through the administrative ranks of the fledgling city-state.</p>

                <h4>The Diplomatic Crisis</h4>
                <p>Moreau's defining moment came during the <a href="#" class="zui-link-inline" data-target="nodeG">Diplomatic Crisis of 2433</a>, when hostilities with the <a href="#" class="zui-link-inline" data-target="nodeC">Mist Sovereigns</a> threatened Aetheria's existence. While others advocated for defensive measures, Moreau took the unprecedented step of traveling into the lower atmosphere of <a href="#" class="zui-link-inline" data-target="nodeD">Jovian-9</a> in a specially reinforced vessel to meet the Sovereigns in their natural environment.</p>

                <h4>Legacy</h4>
                <p>After three days of negotiations (during which many feared him dead), Moreau emerged with the framework for the Atmospheric Accord, establishing territorial boundaries, communication protocols, and resource-sharing agreements that have maintained peace ever since. His innovative approach to interspecies diplomacy has been studied at academies throughout the solar system, and his development of a simplified symbolic language for communication with the Sovereigns laid the groundwork for more sophisticated exchanges. Moreau continues to serve as an elder advisor to the <a href="#" class="zui-link-inline" data-target="nodeG">Council</a> and maintains a personal friendship with several Sovereign entities.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeP',
          data: {
            title: 'Earth Temporal Regulations Commission',
            color: 'rgb(30, 100, 130)',
            content: `
              <div class="wiki-content">
                <h3>Earth Temporal Regulations Commission</h3>
                <div class="summary">A powerful regulatory body on Earth dedicated to monitoring and controlling temporal research and technologies across human settlements in the solar system.</div>

                <p>Established in 2420 following early <a href="#" class="zui-link-inline" data-target="nodeB">foldspace</a> experiments that produced concerning temporal anomalies, the Earth Temporal Regulations Commission (ETRC) oversees all research related to time manipulation, dilation, and observation. Operating under the authority of the United Earth Government, the Commission has broad powers to restrict or shut down technologies deemed to pose risks to temporal stability.</p>

                <h4>Conflict with Aetheria</h4>
                <p>The ETRC has repeatedly attempted to assert jurisdiction over <a href="#" class="zui-link-inline" data-target="nodeA">Aetherian</a> <a href="#" class="zui-link-inline" data-target="nodeI">temporal communications</a> research, claiming that experiments conducted by <a href="#" class="zui-link-inline" data-target="nodeH">Dr. Elara Venn</a> and her team could potentially create cascading causality violations affecting the entire solar system. The <a href="#" class="zui-link-inline" data-target="nodeG">Atmospheric Council</a> has firmly rejected these claims, citing Aetheria's sovereign status and the technology's importance to their independent development.</p>

                <h4>Current Status</h4>
                <p>While lacking direct enforcement capability in Aetherian space, the ETRC maintains a permanent observer presence aboard the <a href="#" class="zui-link-inline" data-target="nodeL">Galileo Research Station</a> and has imposed trade restrictions on temporal technology exports from Aetheria to Earth-controlled territories. Recent diplomatic initiatives suggest a possible compromise may be developing, with Aetheria offering to share safety protocols in exchange for reduced oversight.</p>
              </div>
            `,
          },
        },
        {
          id: 'nodeQ',
          data: {
            title: 'Aria Xiang',
            color: 'rgb(180, 120, 60)',
            content: `
              <div class="wiki-content">
                <h3>Aria Xiang</h3>
                <div class="summary">Groundbreaking composer and sound engineer who pioneered the translation of Mist Sovereign Storm Poetry into auditory experiences accessible to human perception.</div>

                <p>Born on the Luna colonies in 2408, Aria Xiang demonstrated synesthetic abilities from childhood, perceiving sounds as complex visual patterns. After training at the New Beijing Conservatory of Electronic Music, she developed revolutionary acoustic translation algorithms that could convert electromagnetic patterns into harmonically complex soundscapes. Upon learning of the <a href="#" class="zui-link-inline" data-target="nodeC">Mist Sovereigns'</a> <a href="#" class="zui-link-inline" data-target="nodeK">Storm Poetry</a>, she immediately recognized the potential for cross-species artistic collaboration.</p>

                <h4>The Tempest Symphony</h4>
                <p>Relocating to <a href="#" class="zui-link-inline" data-target="nodeA">Aetheria</a> in 2436, Xiang spent three years working with a collective of Sovereign artists to create "The Tempest Symphony," a groundbreaking performance that combined traditional orchestral instruments with real-time translations of electrical storm patterns. The premiere performance in the <a href="#" class="zui-link-inline" data-target="nodeE">Central Nexus</a> grand hall featured a section where the building's <a href="#" class="zui-link-inline" data-target="nodeF">crystalline structure</a> itself served as a resonating instrument, vibrating in response to precisely calibrated atmospheric disturbances.</p>

                <h4>Legacy and Influence</h4>
                <p>Xiang's innovations have opened new frontiers in interspecies communication, demonstrating that art can bridge perceptual gaps that logic and language cannot. Her current project involves developing permanent sound installation chambers in the <a href="#" class="zui-link-inline" data-target="nodeN">Suspended Gardens</a>, where visitors can experience continuous translations of the natural atmospheric patterns of <a href="#" class="zui-link-inline" data-target="nodeD">Jovian-9</a>, including the majestic harmonic structures that emerge from the Great Red Vortex.</p>
              </div>
            `,
          },
        },
      ];

      // Function to create a node element
      function createNodeElement(nodeId, instanceId, isSimple = false) {
        // Get the node from the Map-based structure instead of direct access
        const node = graph.nodes.get(nodeId);
        if (!node) return null;

        // Create the node element with performance optimizations
        const nodeElement = document.createElement('div');
        nodeElement.classList.add('node');
        nodeElement.id = `node-${nodeId}-${instanceId}`;
        nodeElement.dataset.nodeId = nodeId;
        nodeElement.dataset.instanceId = instanceId;
        nodeElement.style.width = `${NODE_WIDTH}px`;
        nodeElement.style.height = `${NODE_HEIGHT}px`;
        nodeElement.style.backgroundColor = node.data.color;

        // If we're creating a simple version, just add the title and return
        if (isSimple) {
          return nodeElement;
        }

        // Create a content container
        const contentElement = document.createElement('div');
        contentElement.classList.add('node-content');

        // Check if we have cached content for this nodeId
        if (!nodeContentCache[nodeId] && node.data.content) {
          // Create a template element to parse the HTML once
          const template = document.createElement('template');
          template.innerHTML = node.data.content.trim();

          // Save the parsed content in the cache
          nodeContentCache[nodeId] = template.content.cloneNode(true);

          // Cache link positions if not already done
          if (!linkPositionsCache[nodeId]) {
            // Create a temporary node to measure link positions
            const tempNode = document.createElement('div');
            tempNode.style.width = `${NODE_WIDTH}px`;
            tempNode.style.height = `${NODE_HEIGHT}px`;
            tempNode.style.position = 'absolute';
            tempNode.style.visibility = 'hidden';
            tempNode.appendChild(template.content.cloneNode(true));
            document.body.appendChild(tempNode);

            // Force layout calculation
            void tempNode.offsetHeight;

            // Find all links and cache their positions
            const links = tempNode.querySelectorAll('.zui-link, .zui-link-inline');
            const positions = {};

            links.forEach((link) => {
              const targetId = link.dataset.target;
              if (targetId) {
                positions[targetId] = getLinkPosition(link, tempNode);
              }
            });

            linkPositionsCache[nodeId] = positions;

            // Clean up
            document.body.removeChild(tempNode);
          }
        }

        // Use cached content if available, otherwise use the node content or ID
        if (nodeContentCache[nodeId]) {
          contentElement.appendChild(nodeContentCache[nodeId].cloneNode(true));
        } else if (node.data.content) {
          contentElement.innerHTML = node.data.content;
        } else {
          contentElement.textContent = nodeId;
        }

        // Setup links to prevent default behavior
        const links = contentElement.querySelectorAll('.zui-link, .zui-link-inline');
        links.forEach((link) => {
          link.addEventListener('click', (e) => {
            e.preventDefault();
          });

          // Set the title without adding visual elements
          const targetId = link.dataset.target;
          if (targetId) {
            link.title = `Links to: ${targetId}`;
          }
        });

        nodeElement.appendChild(contentElement);
        return nodeElement;
      }

      /**
       * Calculates relative position of a link within its parent node
       * @param {HTMLElement} linkElement - The link element
       * @param {HTMLElement} nodeElement - The parent node element
       * @returns {Object} - The relative position and size data
       */
      function getLinkPosition(linkElement, nodeElement) {
        const nodeRect = nodeElement.getBoundingClientRect();
        const linkRect = linkElement.getBoundingClientRect();

        // Calculate relative position (0-1) within the node
        const x = (linkRect.left + linkRect.width / 2 - nodeRect.left) / nodeRect.width;
        const y = (linkRect.top + linkRect.height / 2 - nodeRect.top) / nodeRect.height;

        // Calculate relative size (0-1) compared to the node
        const width = linkRect.width / nodeRect.width;
        const height = linkRect.height / nodeRect.height;

        return { x, y, width, height };
      }

      // Converts link position to a transform matrix
      function linkPositionToTransform(linkPosition) {
        // Calculate the scale factor to make the target node exactly match the link size
        // We need to scale the node (NODE_WIDTH x NODE_HEIGHT) to match the link's dimensions
        const scaleX = linkPosition.width;
        const scaleY = linkPosition.height;

        // The link's center position in the node's coordinate system
        // Convert from relative coordinates (where linkPosition.x,linkPosition.y is the relative center)
        // to the coordinate system of the source node (where 0,0 is center)
        const translateX = (linkPosition.x - 0.5) * NODE_WIDTH;
        const translateY = (linkPosition.y - 0.5) * NODE_HEIGHT;

        // Create the transform matrix
        // First translate to position, then scale to match the link's size
        return new DOMMatrix().translate(translateX, translateY).scale(scaleX, scaleY);
      }

      // Generate a unique ID for an edge
      function generateEdgeId(source, target) {
        return `e_${source}_${target}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
      }

      // Setup temporary DOM to find links and build the edge map
      function buildEdgesFromLinks() {
        // If we already have cached link positions for all nodes, use them directly
        if (Object.keys(linkPositionsCache).length === nodesArray.length) {
          const edges = [];

          // Generate edges from cached positions
          for (const node of nodesArray) {
            const nodeId = node.id;
            const positions = linkPositionsCache[nodeId];

            if (positions) {
              for (const [targetId, linkPosition] of Object.entries(positions)) {
                edges.push({
                  id: generateEdgeId(nodeId, targetId),
                  source: nodeId,
                  target: targetId,
                  transform: linkPositionToTransform(linkPosition),
                });
              }
            }
          }

          return edges;
        }

        // If cache is incomplete, fall back to the original method
        // Create a temporary container
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.visibility = 'hidden';
        tempContainer.style['pointer-events'] = 'none';
        document.body.appendChild(tempContainer);

        // Generate the edges array
        const edges = [];
        const renderedNodes = {};

        // First, create a DOM element for each node
        for (const node of nodesArray) {
          const nodeElement = document.createElement('div');
          nodeElement.classList.add('node');
          nodeElement.style.width = `${NODE_WIDTH}px`;
          nodeElement.style.height = `${NODE_HEIGHT}px`;

          const contentElement = document.createElement('div');
          contentElement.classList.add('node-content');
          contentElement.innerHTML = node.data.content;

          nodeElement.appendChild(contentElement);
          tempContainer.appendChild(nodeElement);
          renderedNodes[node.id] = nodeElement;
        }

        // Force layout calculation
        void tempContainer.offsetHeight;

        // Now find all links and calculate their positions
        for (const [nodeId, nodeElement] of Object.entries(renderedNodes)) {
          const links = nodeElement.querySelectorAll('.zui-link, .zui-link-inline');

          // Create cache entry for this node if it doesn't exist
          if (!linkPositionsCache[nodeId]) {
            linkPositionsCache[nodeId] = {};
          }

          links.forEach((link) => {
            const targetId = link.dataset.target;
            if (!targetId || !renderedNodes[targetId]) return;

            // Get the link's position relative to its parent node
            const linkPosition = getLinkPosition(link, nodeElement);

            // Cache the position for future use
            linkPositionsCache[nodeId][targetId] = linkPosition;

            // Convert to a transform matrix
            const transform = linkPositionToTransform(linkPosition);

            // Add to edges array with a unique ID
            edges.push({
              id: generateEdgeId(nodeId, targetId),
              source: nodeId,
              target: targetId,
              transform,
            });
          });
        }

        // Clean up
        document.body.removeChild(tempContainer);

        return edges;
      }

      // First, build edges from links in node content
      const edgesArray = buildEdgesFromLinks();
      console.log(nodesArray, edgesArray, linkPositionsCache);
      // Define size threshold for showing content (as a proportion of original size)
      const CONTENT_VISIBILITY_THRESHOLD = 0.005;

      const container = document.getElementById('zui-container');

      // Create the shifting origin graph with our nodes and edges
      const graph = new ShiftingOriginGraph(nodesArray, edgesArray, 'nodeA', 50);

      // Define zoom threshold callbacks
      function shouldShiftOrigin(combinedTransform, containerWidth, containerHeight, node) {
        // Check if screen is completely covered by the node
        return isScreenCoveredByRectangle(
          { x: -NODE_WIDTH / 2, y: -NODE_HEIGHT / 2, width: NODE_WIDTH, height: NODE_HEIGHT },
          combinedTransform,
          containerWidth,
          containerHeight,
        );
      }

      function shouldUnshiftOrigin(originTransform, containerWidth, containerHeight) {
        // For zooming out, we check if the current reference node no longer covers the screen
        return !isScreenCoveredByRectangle(
          { x: -NODE_WIDTH / 2, y: -NODE_HEIGHT / 2, width: NODE_WIDTH, height: NODE_HEIGHT },
          originTransform,
          containerWidth,
          containerHeight,
        );
      }

      /**
       * Determines whether a node should be culled based on its position and size
       * @param {ZoomNode} node - The node object
       * @param {DOMMatrix} transform - The transform from reference node to this node
       * @param {DOMMatrix} originTransform - The current viewport transform
       * @returns {boolean} true if the node should be culled (invisible), false if visible
       */
      function nodeCullingCallback(node, transform, originTransform) {
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        const padding = 0.5; // Add 50% padding around viewport to account for nodes partially visible

        // Combine viewport transform with the node's transform
        const combinedTransform = originTransform.multiply(transform);

        // Get the scale of the node
        const scale = combinedTransform.a; // Assuming uniform scaling

        // Calculate the node's position in viewport coordinates
        const nodeX = containerWidth / 2 + combinedTransform.e;
        const nodeY = containerHeight / 2 + combinedTransform.f;

        // Calculate the scaled dimensions of the node
        const scaledWidth = NODE_WIDTH * scale;
        const scaledHeight = NODE_HEIGHT * scale;

        // Define the extended viewport bounds with padding
        const minVisibleX = -containerWidth * padding;
        const maxVisibleX = containerWidth * (1 + padding);
        const minVisibleY = -containerHeight * padding;
        const maxVisibleY = containerHeight * (1 + padding);

        // Check if node is outside the viewport - use a bounding box test
        return !(
          nodeX + scaledWidth / 2 >= minVisibleX &&
          nodeX - scaledWidth / 2 <= maxVisibleX &&
          nodeY + scaledHeight / 2 >= minVisibleY &&
          nodeY - scaledHeight / 2 <= maxVisibleY
        );
      }

      // Function to update the DOM based on the current graph state
      function updateDOM() {
        // Get container dimensions for centering
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        // Update reference node tracking
        const currentOriginNodeId = graph.originNode.id;
        if (lastOriginNodeId !== currentOriginNodeId) {
          // Reference node changed, reset zoom level
          currentZoomLevel = 1.0;
          lastOriginNodeId = currentOriginNodeId;
        }

        // Set to track active elements in this update
        const activeElements = new Set();

        // Process all visible nodes using the culling callback
        const visibleNodes = [];

        // Get nodes from graph with transforms using our culling callback
        for (const { node, transform } of graph.getUnfoldedTree(nodeCullingCallback)) {
          // Combine viewport transform with the node's transform
          const combinedTransform = graph.originTransform.multiply(transform);

          // Get the scale of the node
          const scale = combinedTransform.a; // Assuming uniform scaling

          // Calculate the node's position in viewport coordinates
          const nodeX = containerWidth / 2 + combinedTransform.e;
          const nodeY = containerHeight / 2 + combinedTransform.f;

          // Add directly to our visible nodes array (culling already done by the graph)
          visibleNodes.push({
            nodeId: node.id,
            node,
            transform,
            combinedTransform,
            nodeX,
            nodeY,
            scale,
          });
        }

        // Process all visible nodes
        for (const { nodeId, node, transform, combinedTransform, nodeX, nodeY, scale } of visibleNodes) {
          // Determine if we should use a simple node based on scale
          const isSimpleNode = scale < CONTENT_VISIBILITY_THRESHOLD;

          // Get a node element from the pool
          const nodeElement = nodePool.get(node.id, isSimpleNode);

          // Add to container if not already there
          if (!nodeElement.parentNode) {
            container.appendChild(nodeElement);
          }

          // Track as active
          activeElements.add(nodeElement);

          // Create a CSS transform
          // The order is important: first translate to center, then apply the transform
          const cssTransform = `
            translate(-50%, -50%)
            translate(${nodeX}px, ${nodeY}px)
            scale(${scale})
          `;

          // Apply the transform
          nodeElement.style.transform = cssTransform;
        }

        // Release unused elements back to the pool
        nodePool.releaseUnused(activeElements);
      }

      // Handle window resizing
      function handleResize() {
        updateDOM();
      }

      // Pan handling
      let isDragging = false;
      let lastX = 0;
      let lastY = 0;

      // Add mouse position tracking
      let currentMouseX = container.clientWidth / 2;
      let currentMouseY = container.clientHeight / 2;

      function setupEventListeners() {
        // Mouse down for panning
        container.addEventListener('mousedown', (e) => {
          // Prevent zooming when clicking on links
          if (e.target.classList.contains('zui-link')) {
            e.preventDefault();
            return;
          }

          isDragging = true;
          lastX = e.clientX;
          lastY = e.clientY;
          container.style.cursor = 'grabbing';
        });

        // Mouse move for panning and tracking
        container.addEventListener('mousemove', (e) => {
          currentMouseX = e.clientX;
          currentMouseY = e.clientY;

          if (isDragging) {
            const dx = e.clientX - lastX;
            const dy = e.clientY - lastY;

            // Update viewport transform for panning
            graph.pan(dx, dy);

            lastX = e.clientX;
            lastY = e.clientY;

            updateDOM();
          }
        });

        // Mouse up to end panning
        window.addEventListener('mouseup', () => {
          if (isDragging) {
            isDragging = false;
            container.style.cursor = 'default';
          }
        });

        // Wheel for zooming
        container.addEventListener(
          'wheel',
          (e) => {
            e.preventDefault();

            // Calculate zoom factor
            const zoomFactor = e.deltaY > 0 ? 0.95 : 1.05;

            // Check if zoom limits would be exceeded
            const newZoomLevel = currentZoomLevel * zoomFactor;
            if (zoomFactor > 1 && newZoomLevel > MAX_ZOOM_LEVEL) {
              // Skip zooming if it would exceed limits
              return;
            }

            // Get mouse position relative to container center
            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;
            const mouseX = e.clientX - containerWidth / 2;
            const mouseY = e.clientY - containerHeight / 2;

            // Apply zoom centered on mouse position and check if reference node changed
            const referenceChanged = graph.zoom(
              mouseX,
              mouseY,
              zoomFactor,
              containerWidth,
              containerHeight,
              shouldShiftOrigin,
              shouldUnshiftOrigin,
            );

            // Update the current zoom level if reference node did not change
            if (!referenceChanged) {
              currentZoomLevel = newZoomLevel;
            } else {
              // Reference node changed, reset zoom level
              currentZoomLevel = 1.0;
            }

            updateDOM();
          },
          { passive: false },
        );

        // Keyboard events for continuous zooming
        window.addEventListener('keydown', (e) => {
          if (e.shiftKey && !e.altKey && !isShiftZooming) {
            isShiftZooming = true;
            startContinuousZoom(true);
          } else if (e.shiftKey && e.altKey && !isAltShiftZooming) {
            isAltShiftZooming = true;
            startContinuousZoom(false);
          }
        });

        window.addEventListener('keyup', (e) => {
          if (!e.shiftKey || (isAltShiftZooming && !e.altKey)) {
            isShiftZooming = false;
            isAltShiftZooming = false;
            stopContinuousZoom();
          }
        });

        // Window resize handler
        window.addEventListener('resize', handleResize);
      }

      // Continuous zoom variables and functions
      let isShiftZooming = false;
      let isAltShiftZooming = false;
      let zoomAnimationId = null;
      let isZooming = false;
      let zoomDirection = 1;
      let lastZoomTime = 0;
      const ZOOM_SPEED = 20;

      function startContinuousZoom(zoomIn) {
        stopContinuousZoom(); // Cancel any existing animation

        isZooming = true;
        zoomDirection = zoomIn ? 1 : -1;
        lastZoomTime = performance.now();

        // Start the animation loop
        animateZoom();
      }

      function animateZoom(currentTime) {
        if (!isZooming) return;

        // Calculate time delta in seconds since last frame
        if (!currentTime) currentTime = performance.now();
        const deltaTime = (currentTime - lastZoomTime) / 1000; // Convert to seconds
        lastZoomTime = currentTime;

        // Calculate framerate-independent zoom factor
        // Base formula: zoom = baseZoom^(speed * deltaTime)
        // For zoom in: baseZoom > 1, for zoom out: baseZoom < 1
        const baseZoomFactor = zoomDirection > 0 ? 1.1 : 0.9;
        const zoomFactor = Math.pow(baseZoomFactor, ZOOM_SPEED * deltaTime);

        // Check if zoom limits would be exceeded
        const newZoomLevel = currentZoomLevel * zoomFactor;
        if (zoomFactor > 1 && newZoomLevel > MAX_ZOOM_LEVEL) {
          // Skip this frame of zooming if it would exceed limits
          zoomAnimationId = requestAnimationFrame(animateZoom);
          return;
        }

        // Get mouse position relative to container center
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        const mouseX = currentMouseX - containerWidth / 2;
        const mouseY = currentMouseY - containerHeight / 2;

        // Apply zoom centered on mouse position and check if reference node changed
        const referenceChanged = graph.zoom(
          mouseX,
          mouseY,
          zoomFactor,
          containerWidth,
          containerHeight,
          shouldShiftOrigin,
          shouldUnshiftOrigin,
        );

        // Update the current zoom level if reference node did not change
        if (!referenceChanged) {
          currentZoomLevel = newZoomLevel;
        } else {
          // Reference node changed, reset zoom level
          currentZoomLevel = 1.0;
        }

        updateDOM();

        // Continue the animation loop
        zoomAnimationId = requestAnimationFrame(animateZoom);
      }

      function stopContinuousZoom() {
        isZooming = false;
        if (zoomAnimationId) {
          cancelAnimationFrame(zoomAnimationId);
          zoomAnimationId = null;
        }
      }

      // Initialize and start the application
      function init() {
        setupEventListeners();
        updateDOM();
      }

      // Start the application
      init();
    